import { useEffect, useRef } from 'react';

/** 入力タグ（input） */
const INPUT_TAG = 'input';
/** 入力タグ（textarea） */
const TEXTAREA_TAG = 'textarea';
/** 入力タグ（select） */
const SELECT_TAG = 'select';
/**  自動フォーカス対処となるinputタグののtype attribute */
const AUTO_FOCUS_INPUT_TYPE = [
  'date',
  'datetime-local',
  'email',
  'month',
  'number',
  'password',
  'search',
  'tel',
  'text',
  'time',
  'url',
  'week',
];

/**
 * @summary モーダル表示時の自動フォーカス用Hooks
 * @param {boolean} show モーダル表示状態
 */
export const useAutoFocus = (show: boolean) => {
  const modalBodyRef = useRef<HTMLDivElement>(null);

  /** モーダルの表示タイミングで自動フォーカスを実行する */
  useEffect(() => {
    if (!show || !modalBodyRef.current) return;
    const firstInputElement = modalBodyRef.current
      .querySelectorAll(`${INPUT_TAG}, ${TEXTAREA_TAG}, ${SELECT_TAG}`)
      .item(0);

    if (!firstInputElement) return;

    switch (firstInputElement.tagName.toLowerCase()) {
      case INPUT_TAG: {
        const input = firstInputElement as HTMLInputElement;
        if (!AUTO_FOCUS_INPUT_TYPE.includes(input.type)) {
          return;
        }
        input.focus();
        const observer = new MutationObserver((mutationList) => {
          mutationList.forEach((mutation) => {
            if (mutation.type !== 'attributes' || mutation.attributeName !== 'disabled') {
              return;
            }

            const target = mutation.target as HTMLInputElement;
            if (target.disabled) return;
            target.focus();
            observer.disconnect();
          });
        });
        observer.observe(input, { attributes: true, attributeFilter: ['disabled'] });
        return;
      }
      case TEXTAREA_TAG: {
        const textarea = firstInputElement as HTMLTextAreaElement;
        textarea.focus();
        textarea.setSelectionRange(textarea.value.length, textarea.value.length);
        const observer = new MutationObserver((mutationList) => {
          mutationList.forEach((mutation) => {
            if (mutation.type !== 'attributes' || mutation.attributeName !== 'disabled') {
              return;
            }

            const target = mutation.target as HTMLTextAreaElement;
            if (target.disabled) return;
            target.focus();
            target.setSelectionRange(target.value.length, target.value.length);
            observer.disconnect();
          });
        });
        observer.observe(textarea, { attributes: true, attributeFilter: ['disabled'] });
        return;
      }
      default:
        return;
    }
  }, [show]);

  return { modalBodyRef };
};
